import { Box, Checkbox, CircularProgress, Collapse, Table, TableBody, TableCell, TableRow } from "@mui/material";
import NombreEditableInput from "@/components/NombreEditableInput";
import dayjs from "dayjs";
import { Fragment, Suspense, memo, useMemo, useState, useCallback, useEffect } from "react";
import { useActualizarEntradaMutation } from "@/redux/api/informeApi";
import { ICarrito, IInforme, velocidades } from "@/redux/models";
import { formatToCurrencyARS } from "@/utils/allUtils";
import { obtenerNombreInforme } from "@/utils/informeUtils";
import { useTablaCarritoV2Props } from "../../logics/useTablaCarritoV2";
import TableInformeHead from "../TableInformeHead";
import InformeAction from "./InformeAction";
import EstadosInformeCell from "./InformeEstadosCell";
import ToolbarInformes from "./ToolbarInformes";

const InformeTableCell = ({ informe, isSelected, handleToggleSelect, context }: { informe: IInforme, isSelected: boolean, handleToggleSelect: (informe: IInforme) => void, context: any }) => {

    const [actualizarEntrada] = useActualizarEntradaMutation();

    const handleChangeEntrada = useCallback(async (entrada: string) => {
        return actualizarEntrada({ id: informe.id, entrada }).unwrap()
            .then(() => context.modificarInforme({ ...informe, entrada }))
    }, [informe, actualizarEntrada, context]);

    const memoizedCelLCheckbox = useMemo(() => (
        <TableCell component="th" scope="row" sx={{ textAlign: 'center' }}>
            <Checkbox
                onChange={() => handleToggleSelect(informe)}
                checked={isSelected}
            />
            <span>
                {informe.id}
            </span>
        </TableCell>
    ), [isSelected, informe, handleToggleSelect]);

    const restCells = useMemo(() => (

        <Fragment>
            <TableCell component="th" scope="row">
                <NombreEditableInput
                    label="Entrada del informe"
                    successMessage="Entrada editada con éxito"
                    editable={true}
                    nombre={informe.entrada}
                    onPressSave={handleChangeEntrada}
                />
            </TableCell>
            <TableCell>
                {dayjs(informe.fechaDeCreacion).format('DD/MM/YYYY')}
            </TableCell>
            <TableCell component="th" scope="row">
                {obtenerNombreInforme(informe, false, { velocidad: true })}
            </TableCell>
            <TableCell>
                {velocidades[informe.velocidad]}
            </TableCell>
            <TableCell>
                {formatToCurrencyARS(informe.precio)}
            </TableCell>
            <TableCell size="small">
                <EstadosInformeCell informe={informe} />
            </TableCell>
            <TableCell>
                <InformeAction viewAs={context.viewAs} setModal={context.setModal} modal={context.modal} informe={informe} modificarInforme={context.modificarInforme} />
            </TableCell>
        </Fragment>
    ), [informe, context, handleChangeEntrada]);

    return (
        <Fragment>
            {memoizedCelLCheckbox}
            {restCells}
        </Fragment>
    );
}

export const LoadingSpinner = () => (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100px' }}>
        <CircularProgress />
    </Box>
);

const TableRowInformes = ({ isOpen, carpeta, informes, context }: { isOpen: boolean, carpeta: ICarrito, informes: IInforme[], context: useTablaCarritoV2Props }) => {

    const [informesSelected, setInformesSelected] = useState<IInforme[]>([]);

    // Reset selected informes when the informes array changes (e.g., after filtering)
    useEffect(() => {
        setInformesSelected([]);
    }, [informes]);

    const handleToggleSelect = useCallback((informe: IInforme) => {
        console.log('informe', informe);
        
        setInformesSelected(prevSelected => {
            const isSelected = prevSelected.some(i => i.id === informe.id);
            
            if (isSelected) {
                const newSelected = prevSelected.filter(i => i.id !== informe.id);
                console.log('informeSelected1', newSelected);
                return newSelected;
            } else {
                const newSelected = [...prevSelected, informe];
                console.log('informeSelected2', newSelected);
                return newSelected;
            }
        });
    }, []);

    const handleSelectAll = useCallback(() => {
        setInformesSelected(prevSelected => {
            if (prevSelected.length === informes.length) {
                return [];
            } else {
                return informes;
            }
        });
    }, [informes]);

    return (
        <TableRow aria-label='informes-de-carrito'
            className={isOpen ? 'bg-color-change' : 'bg-color-reset'}>
            <TableCell style={{
                paddingBottom: 0, paddingTop: 0,
                borderBottom: isOpen ? 'solid 10px var(--row-informes-open-color)' : 'none',
            }} colSpan={8}>
                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                    <Box sx={(sx) => ({
                        bgcolor: 'background.paper',
                        p: 1,
                        mb: 1,
                        border: '1px solid rgba(0, 0, 0, 0.12)',
                        boxShadow: sx.palette.mode === 'light' ? `inset 0px 0px 12px 0px #9c9c9c` : 'none',
                    })}>

                        <ToolbarInformes selected={informesSelected} viewAs={context.viewAs} setModal={context.setModal} carritos={context.selectedCarritos} />

                        <Table size="small" aria-label="informesExpandibles">
                            <TableInformeHead
                                handleSelectAll={handleSelectAll}
                                checked={informesSelected.length === informes.length}
                            />
                            <TableBody>
                                <Suspense fallback={<LoadingSpinner />}>
                                    {

                                        informes.map((informe: IInforme, i: number) => (
                                            <TableRow sx={(theme) => {
                                                return {
                                                    backgroundColor: informe.printed ? theme.palette.mode === 'light' ? '#dcffde' : '#263b2761' : 'inherit',
                                                }
                                            }}>
                                                <InformeTableCell
                                                    informe={informe}
                                                    isSelected={informesSelected.some(i => i.id === informe.id)}
                                                    handleToggleSelect={handleToggleSelect}
                                                    context={context}
                                                />
                                            </TableRow>
                                        ))}
                                </Suspense>
                            </TableBody>
                        </Table>
                    </Box>
                </Collapse>
            </TableCell>
        </TableRow >
    );
}

export default memo(TableRowInformes, (prevProps, nextProps) => {
    return prevProps.isOpen === nextProps.isOpen && 
           prevProps.informes.length === nextProps.informes.length &&
           prevProps.informes === nextProps.informes &&
           prevProps.context.informes === nextProps.context.informes;
});
